import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import PageTransition from '@/components/PageTransition';
import { PrivacyConsentGate } from '@/components/PrivacyConsentGate';
import { AnalyticsConsentBanner } from '@/components/AnalyticsConsentBanner';
import LandingPage from '@/pages/LandingPage';
import SignupPage from '@/pages/SignupPage';
import LoginPage from '@/pages/LoginPage';
import UserDashboard from '@/pages/UserDashboard';
import UserProfilePage from '@/pages/UserProfilePage';
import AdminDashboard from '@/pages/AdminDashboard';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import LoginHelpPage from '@/pages/LoginHelpPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import MyInsulPage from '@/pages/MyInsulPage';
import ProgramDetailPage from '@/pages/ProgramDetailPage';
import SubscriptionSuccessPage from '@/pages/SubscriptionSuccessPage';
import LowSaltProgramPage from '@/pages/LowSaltProgramPage';
import SugarHomeostasisProgramPage from '@/pages/SugarHomeostasisProgramPage';
import LipidTargetProgramPage from '@/pages/LipidTargetProgramPage';
import FluidBalanceProgramPage from '@/pages/FluidBalanceProgramPage';
import HypertensionDetailPage from '@/pages/HypertensionDetailPage';
import ContactUsPage from '@/pages/ContactUsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import RestoreAccountPage from '@/pages/RestoreAccountPage';
import InsulRewardsPage from '@/pages/InsulRewardsPage';
import WellnessMarkersPage from '@/pages/WellnessMarkersPage';
import MicronutrientDetailPage from '@/pages/MicronutrientDetailPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import DisclaimerPage from '@/pages/DisclaimerPage';
import TermsOfUsePage from '@/pages/TermsOfUsePage';
import CookieSettingsPage from '@/pages/CookieSettingsPage';
// Admin Auth Pages (Supabase-based with MFA) - Single consolidated page
import AdminLoginPage from '@/pages/AdminLoginPage';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.needs_privacy_consent) {
    return <PrivacyConsentGate />;
  }
  
  // ph-no-capture: every signed-in area (diary, meals, health details, profile, payments) is excluded from analytics autocapture
  return <div className="ph-no-capture" data-testid="ph-no-capture-protected">{children}</div>;
};

const AdminRoute = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isValidAdmin, setIsValidAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminSession = async () => {
      const adminToken = sessionStorage.getItem('admin_session_token');
      
      if (!adminToken) {
        // No admin session - redirect to admin login
        navigate('/admin-login', { replace: true });
        return;
      }

      try {
        // Verify admin session with backend
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-auth/session`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });

        if (response.ok) {
          setIsValidAdmin(true);
        } else {
          // Invalid or expired token
          sessionStorage.removeItem('admin_session_token');
          navigate('/admin-login', { replace: true });
        }
      } catch (error) {
        sessionStorage.removeItem('admin_session_token');
        navigate('/admin-login', { replace: true });
      } finally {
        setChecking(false);
      }
    };

    checkAdminSession();
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Verifying admin session...</p>
        </div>
      </div>
    );
  }

  if (!isValidAdmin) {
    return null;
  }

  return <div className="ph-no-capture" data-testid="ph-no-capture-admin">{children}</div>;
};


function AppRoutes() {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
        <Route path="/signup" element={<PageTransition><SignupPage /></PageTransition>} />
        <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicyPage /></PageTransition>} />
        <Route path="/disclaimer" element={<PageTransition><DisclaimerPage /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><TermsOfUsePage /></PageTransition>} />
        <Route path="/cookie-settings" element={<PageTransition><CookieSettingsPage /></PageTransition>} />
        <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
        <Route path="/login-help" element={<PageTransition><LoginHelpPage /></PageTransition>} />
        <Route path="/recover" element={<Navigate to="/forgot-password" replace />} />
        <Route path="/forgot-password" element={<PageTransition><ForgotPasswordPage /></PageTransition>} />
        <Route path="/verify-email" element={<PageTransition><VerifyEmailPage /></PageTransition>} />
        <Route path="/restore-account" element={<PageTransition><RestoreAccountPage /></PageTransition>} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <PageTransition><UserDashboard /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <PageTransition><UserProfilePage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rewards"
          element={
            <ProtectedRoute>
              <PageTransition><InsulRewardsPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <PageTransition><AdminDashboard /></PageTransition>
            </AdminRoute>
          }
        />
        {/* Admin Auth Routes (Supabase-based with MFA) - Single page handles all steps */}
        <Route path="/admin-login" element={<AdminLoginPage />} />
        {/* Legacy routes redirect to consolidated admin login */}
        <Route path="/admin-mfa-enroll" element={<Navigate to="/admin-login" replace />} />
        <Route path="/admin-mfa-verify" element={<Navigate to="/admin-login" replace />} />
        {/* MyInsul Routes */}
        <Route
          path="/myinsul"
          element={
            <ProtectedRoute>
              <PageTransition><MyInsulPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/program/:programId"
          element={
            <ProtectedRoute>
              <PageTransition><ProgramDetailPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/subscription-success"
          element={
            <ProtectedRoute>
              <PageTransition><SubscriptionSuccessPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/category/cdm/low-salt"
          element={
            <ProtectedRoute>
              <PageTransition><LowSaltProgramPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/category/cdm/sugar-homeostasis"
          element={
            <ProtectedRoute>
              <PageTransition><SugarHomeostasisProgramPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/category/cdm/lipid-target"
          element={
            <ProtectedRoute>
              <PageTransition><LipidTargetProgramPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/category/cdm/fluid-balance"
          element={
            <ProtectedRoute>
              <PageTransition><FluidBalanceProgramPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/wellness-markers"
          element={
            <ProtectedRoute>
              <PageTransition><WellnessMarkersPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myinsul/wellness-markers/:nutrientId"
          element={
            <ProtectedRoute>
              <PageTransition><MicronutrientDetailPage /></PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact-us"
          element={
            <PageTransition><ContactUsPage /></PageTransition>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  // Country gate (17 Oct 2026): checked only on the sign-up screen (SignupPage) - never on every page load
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
        <AnalyticsConsentBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;